import { TextButton } from '@wix/design-system'
import { ListContentComponent, useContent, useMetadata, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { getDomainFromUrl } from '@wix/funnel-utils'
import { ArrowLeft, ChevronLeftLarge } from '@wix/wix-ui-icons-common/lazy'
import React, { FC, useEffect, useState } from 'react'
import { v4 as randomUUID } from 'uuid'

import { usePageLogger } from '../../../bi/mfi'
import { useFlowContext } from '../../../hooks/useFlowContext'
import { useViewport } from '../../../hooks/useViewport'
import { EnterpriseLogos } from '../../reusable/EnterpriseLogos'
import { EnterpriseLogosJa } from '../../reusable/EnterpriseLogosJa'
import { EnterpriseLayout } from '../EnterpriseLayout'
import { EnterpriseFormRenderer } from './EnterpriseFormRenderer'
import s from './index.scss'

const PRODUCT_INTERESTED_IN: Record<string, string> = {
  product_sso: 'SSO',
  product_single_site: 'Single site',
  product_multiple_site_management: 'Multiple site management',
  product_landing_pages: 'Landing pages',
  product_custom_dev: 'Custom dev / integrations',
  product_advanced_security: 'Advanced security',
  product_franchise_tools: 'Franchise tools',
  product_enterprise_grade_bookings: 'Enterprise Grade Bookings',
  product_ecom: 'Enterprise Grade eCommerce',
  product_enterprise: 'Full enterprise solution',
  product_other: 'Other',
}

const DEPARTMENT: Record<string, string> = {
  department_operations: 'Operations',
  department_marketing: 'Marketing',
  department_product: 'Product',
  department_management: 'Management',
  department_it: 'IT',
  department_other: 'Other',
}

const COMPANY_SIZE: Record<string, string> = {
  size_1_50: '1-50 employees',
  size_51_100: '51-100 employees',
  size_101_500: '101-500 employees',
  size_501_1000: '501-1000 employees',
  size_1001_5000: '1001-5000 employees',
  size_5000_plus: '5000 employees',
}

type Field = {
  type: 'radio' | 'checkbox' | 'input' | 'textarea' | 'phone' | 'url' | 'email'
  key: string
  label: string
  placeholder: string
}

export type Screen = {
  fields: ListContentComponent<Field>
  agreement: string
  description: string
  title: string
  biQuestionKey: string
}

export type Content = {
  title: string
  text: string
  back: string
  submit: string
  next: string
  logos: string
  checkboxesValidationError: string
  emptyValidationError: string
  phoneValidationError: string
  urlValidationError: string
  emailValidationError: string
  phoneValidationErrorMobile: string
  emailValidationErrorMobile: string
  urlValidationErrorMobile: string
  agreementValidationError: string
  screens: ListContentComponent<Screen>
}

type Answers = Record<string, any>

const PII_KEYS = ['first_name', 'last_name', 'phone_number']

export const EnterpriseForm: FC = () => {
  const [{ language }] = useMetadata()
  const [flowContext] = useFlowContext()
  const { viewport } = useViewport()
  const next = useNext()
  const { content } = useContent<Content>()
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Answers>({})
  const pageLogger = usePageLogger()
  const { screens } = content
  const [isPageLoading] = usePageLoading()

  const stepsCount = screens.items.length
  const screen = screens.items[step]
  const screenOptionKeys = screen.fields.items.map(f => f.key)

  useEffect(() => {
    pageLogger.screenView({ questionName: screen.biQuestionKey })
  }, [pageLogger, screen])

  useEffect(() => {
    // preload intl-tel-input utils
    const id = 'iti-script'
    const script = document.createElement('script')

    script.src = window.__ITI_PATH__
    script.id = id

    if (!document.querySelector(`#${id}`)) {
      document.body.appendChild(script)
    }
  }, [])

  const onBack = () => {
    pageLogger.back({ questionName: screen.biQuestionKey })
    setStep(s => s - 1)
  }

  const processAnswers = () => {
    return screens.items.reduce<Record<string, { values: string[] }>>((acc, i: Screen) => {
      const fields = i.fields
      const isCheckboxes = fields.items.every(i => i.type === 'checkbox')
      const isRadiogroup = fields.items.every(i => i.type === 'radio')

      if (isCheckboxes || isRadiogroup) {
        acc[i.biQuestionKey] = {
          values: [
            fields.items
              .filter(f => answers[f.key])
              .map(f => {
                if (i.biQuestionKey === 'product_interested_in') {
                  return PRODUCT_INTERESTED_IN[f.key]
                } else if (i.biQuestionKey === 'department') {
                  return DEPARTMENT[f.key]
                } else if (i.biQuestionKey === 'company_size') {
                  return COMPANY_SIZE[f.key]
                } else {
                  return f.key
                }
              })
              .join(';'),
          ],
        }
      } else {
        fields.items.forEach(f => {
          if (f.type === 'phone') {
            const [{ dialCode }, number] = answers[f.key]
            acc[f.key] = { values: [`${dialCode}${number}`] }
          } else {
            acc[f.key] = { values: [answers[f.key]] }
          }
        })
      }

      return acc
    }, {})
  }

  const onNext = async () => {
    if (isPageLoading) {
      return
    }

    const isCheckboxes = screen.fields.items.every(i => i.type === 'checkbox')
    const isRadiogroup = screen.fields.items.every(i => i.type === 'radio')
    let biAnswer: Record<string, string> | string = {}

    if (isCheckboxes || isRadiogroup) {
      biAnswer = screen.fields.items
        .filter(f => answers[f.key])
        .map(f => f.key)
        .join()
    } else {
      biAnswer = screenOptionKeys.reduce((acc: Record<string, string>, key: string) => {
        acc[key] = PII_KEYS.includes(key) ? null : answers[key]
        if (key === 'email') {
          acc[key] = acc[key].split('@')?.[1] ?? null
        }
        if (key === 'company_url') {
          acc.company_domain = getDomainFromUrl(acc[key])
        }
        return acc
      }, {})
    }

    const submissionId = flowContext?.metadata.submissionId || randomUUID()

    if (Object.values(biAnswer).length > 0) {
      pageLogger.answer({
        answer: biAnswer,
        questionName: screen.biQuestionKey,
        additionalInfo: submissionId,
      })
    }

    if (step === stepsCount - 1) {
      next(processAnswers(), { submissionId })
    } else {
      setStep(s => s + 1)
    }
  }

  return (
    <EnterpriseLayout>
      <div className={s.EnterpriseForm}>
        <div className={s.EnterpriseForm__hero}>
          <div className={s.EnterpriseForm__title}>{content.title}</div>
          <div className={s.EnterpriseForm__subtitle}>{content.text}</div>
        </div>
        <div className={s.EnterpriseForm__logos}>
          {content.logos}
          {language === 'ja' ? <EnterpriseLogosJa /> : <EnterpriseLogos />}
        </div>
        <div className={s.EnterpriseForm__content}>
          <EnterpriseFormRenderer
            answers={answers}
            setAnswers={setAnswers}
            key={screens.items[step].title}
            screen={screens.items[step]}
            onNext={onNext}
            step={step}
            stepsCount={stepsCount}
          />
          {step !== 0 && (
            <div className={s.EnterpriseForm__backButton}>
              {viewport === 'mobile' ? (
                <TextButton prefixIcon={<ChevronLeftLarge />} skin="dark" onClick={onBack} />
              ) : (
                <TextButton prefixIcon={<ArrowLeft />} skin="dark" onClick={onBack}>
                  {content.back}
                </TextButton>
              )}
            </div>
          )}
        </div>
      </div>
    </EnterpriseLayout>
  )
}
